import { useState, useMemo, useEffect } from "react"
import { ITableColumnsType } from "../types"
import { UseUserPreferencesReturn } from "./use-user-preferences"

export interface UseColumnsPickerReturn<T = any> {
    visibleColumns: ITableColumnsType<T>
    onColumnsSelected: (visibleColumnKeys: React.Key[]) => void
}

interface UseColumnsPickerOptions<T = any> {
    initialColumns: ITableColumnsType<T>
    preferences: UseUserPreferencesReturn
}

export function useColumnsPicker<T = any>(options: UseColumnsPickerOptions): UseColumnsPickerReturn<T> {
    const { initialColumns, preferences } = options
    const [hiddenColumns, setHiddenColumns] = useState<Set<React.Key>>(new Set())

    // Update hidden columns when preferences are loaded or reset
    useEffect(() => {
        const savedHiddenColumns = preferences?.state.hiddenColumns
        if (savedHiddenColumns && Array.isArray(savedHiddenColumns)) {
            if (savedHiddenColumns.length === 0) {
                // Preferences were reset, clear all hidden columns
                setHiddenColumns(new Set())
            } else {
                setHiddenColumns(new Set(savedHiddenColumns))
            }
        }
    }, [preferences?.state.hiddenColumns])

    const visibleColumns = useMemo(() => {
        // Convert array of records to a single map
        const fixedColumnsMap = new Map<string, "left" | "right">()
        preferences?.state.fixedColumns?.forEach((record) => {
            Object.entries(record).forEach(([key, value]) => {
                fixedColumnsMap.set(key, value)
            })
        })

        return initialColumns.map((col) => {
            // If column has a hardcoded fixed value, always use it
            // Otherwise, check preferences
            const fixedValue = col.fixed || fixedColumnsMap.get(col.key.toString()) || false

            return {
                ...col,
                hidden: hiddenColumns.has(col.key),
                fixed: fixedValue
            }
        })
    }, [initialColumns, hiddenColumns, preferences?.state.fixedColumns])

    const onColumnsSelected = (visibleColumnKeys: React.Key[]) => {
        const newHiddenColumns = new Set<React.Key>()
        initialColumns.forEach((col) => {
            if (!visibleColumnKeys.includes(col.key)) {
                newHiddenColumns.add(col.key)
            }
        })
        setHiddenColumns(newHiddenColumns)

        // Update preferences directly
        if (preferences) {
            const hiddenColumnsArray = Array.from(newHiddenColumns) as string[]
            preferences.setHiddenColumns(hiddenColumnsArray)
        }
    }

    return {
        visibleColumns,
        onColumnsSelected
    }
}
